import React, { useState, useEffect } from 'react';
import PhraseSentimentAnalysis from './PhraseSentimentAnalysis';
import CompetitiveBenchmarking from './CompititiveBenchmarking';
import TopicClusteringVisualization from './TopicClusterVisualisation';
import WordAssociationNetwork from './WordAssociationNetwork';

const CategoryPerformance = () => {
  // Use mock data from global window object
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Load mock data on component mount
  useEffect(() => {
    if (window.livpureMockData) {
      setReviews(window.livpureMockData.reviews);
      setCategories(window.livpureMockData.categories);
    }
  }, []);

  // Mock data for category performance
  const defaultCategories = [
    { name: 'Customer Service', mentioned: 387, positivePercentage: 62, trend: 'up' },
    { name: 'Product Quality', mentioned: 356, positivePercentage: 78, trend: 'stable' },
    { name: 'Delivery & Shipping', mentioned: 312, positivePercentage: 43, trend: 'down' },
    { name: 'App Experience', mentioned: 289, positivePercentage: 48, trend: 'down' },
    { name: 'Price & Value', mentioned: 267, positivePercentage: 65, trend: 'up' },
    { name: 'Product Effectiveness', mentioned: 187, positivePercentage: 71, trend: 'stable' },
    { name: 'Store Experience', mentioned: 156, positivePercentage: 54, trend: 'stable' },
    { name: 'Packaging', mentioned: 142, positivePercentage: 58, trend: 'up' },
    { name: 'Side Effects', mentioned: 132, positivePercentage: 82, trend: 'up' },
    { name: 'Ease of Use', mentioned: 98, positivePercentage: 37, trend: 'down' },
  ];
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeView, setActiveView] = useState(null);
  const [activeTab, setActiveTab] = useState('most-mentioned');
  
  const topPositive = [...defaultCategories].sort((a, b) => b.positivePercentage - a.positivePercentage).slice(0, 6);
  const topNegative = [...defaultCategories].sort((a, b) => a.positivePercentage - b.positivePercentage).slice(0, 6);
  
  // Helper function for trend icons/colors
  const getTrendElement = (trend) => {
    switch (trend) {
      case 'up':
        return <span className="text-green-500">↑</span>;
      case 'down':
        return <span className="text-red-500">↓</span>;
      case 'stable':
      default:
        return <span className="text-gray-500">→</span>;
    }
  };
  
  // Handle category click
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setActiveView('phrase-sentiment');
  };
  
  // Render appropriate view based on selected view
  const renderDetailView = () => {
    if (!selectedCategory) return null;
    
    switch (activeView) {
      case 'phrase-sentiment':
        return (
          <PhraseSentimentAnalysis 
            category={selectedCategory} 
            reviews={reviews} 
          />
        );
      case 'competitive-benchmarking':
        return (
          <CompetitiveBenchmarking 
            category={selectedCategory} 
            reviews={reviews}
            allCategories={categories}
          />
        );
      case 'topic-clustering':
        return (
          <TopicClusteringVisualization 
            category={selectedCategory} 
            reviews={reviews} 
          />
        );
      case 'word-association':
        return (
          <WordAssociationNetwork 
            category={selectedCategory} 
            reviews={reviews} 
          />
        );
      default:
        return null;
    }
  };
  
  return (
    <div className="">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Categories Column */}
        <div className={`md:col-span-${selectedCategory ? "1" : "3"} bg-white rounded-lg shadow-sm p-5`}>
          <h2 className="text-lg font-medium text-gray-800 mb-4">Category Performance</h2>
          
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="-mb-px flex">
              <button
                className={`mr-8 py-2 px-1 text-sm font-medium ${
                  activeTab === 'most-mentioned'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('most-mentioned')}
              >
                Most Mentioned
              </button>
              <button
                className={`mr-8 py-2 px-1 text-sm font-medium ${
                  activeTab === 'top-positive'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('top-positive')}
              >
                Top Positive
              </button>
              <button
                className={`py-2 px-1 text-sm font-medium ${
                  activeTab === 'top-negative'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('top-negative')}
              >
                Top Negative
              </button>
            </nav>
          </div>
          
          {/* Category Grid */}
          <div className={`grid grid-cols-${selectedCategory ? "1": "3"} gap-4`}>
            {(activeTab === 'most-mentioned' ? defaultCategories.slice(0, 6) : 
              activeTab === 'top-positive' ? topPositive : 
              topNegative).map((category, index) => (
              <div 
                key={index} 
                className={`${selectedCategory === category.name ? "bg-blue-100 border-blue-200": " border-gray-100"} flex items-center border  rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer group`}
                onClick={() => handleCategoryClick(category.name)}
              >
                <div className="flex-grow">
                  <div className="flex items-center">
                    <h3 className="font-medium text-sm group-hover:text-blue-600">{category.name}</h3>
                    <span className="ml-1 text-xs text-gray-500">{getTrendElement(category.trend)}</span>
                  </div>
                  <div className="text-xs text-gray-500">Mentioned in {category.mentioned} reviews</div>
                  <div className="mt-2 flex items-center">
                    <div className="flex-grow bg-gray-100 h-2 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${category.positivePercentage >= 60 ? 'bg-green-500' : category.positivePercentage >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${category.positivePercentage}%` }}
                      ></div>
                    </div>
                    <div className="ml-2 text-xs font-medium">{category.positivePercentage}%</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Details Column */}
        
          {selectedCategory && (
            <div className="md:col-span-2"><div>
              {/* View Selection Tabs */}
              <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                <div className="flex space-x-4 border-b border-gray-200 pb-2 mb-4">
                  <button
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      activeView === 'phrase-sentiment' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={() => setActiveView('phrase-sentiment')}
                  >
                    Phrase Sentiment
                  </button>
                  <button
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      activeView === 'competitive-benchmarking' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={() => setActiveView('competitive-benchmarking')}
                  >
                    Competitive Benchmark
                  </button>
                  <button
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      activeView === 'topic-clustering' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={() => setActiveView('topic-clustering')}
                  >
                    Topic Clusters
                  </button>
                  <button
                    className={`py-2 px-3 text-sm font-medium rounded-md ${
                      activeView === 'word-association' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={() => setActiveView('word-association')}
                  >
                    Word Association
                  </button>
                </div>
                
                {/* Detailed View Rendering */}
                {renderDetailView()}
              </div>
            </div>
        </div>
          )}
      </div>
    </div>
  );
};

export default CategoryPerformance;